/**
 * Time display.
 *
 * Everything is stored and transported as UTC ISO-8601. Nothing in either app
 * ever sends a naive local timestamp to the API, and nothing ever renders a raw
 * UTC string to a person.
 *
 * Which zone to render in differs by audience, and getting this wrong is how a
 * captain shows up a day early:
 *
 * - **Admin app** always renders Europe/Oslo. ABG staff are in Bergen, the
 *   harbourmaster they are emailing is in Norway, and a single fixed office
 *   zone is what makes two staff members reading the same queue agree.
 * - **Captain app** renders the device's own zone by default, because a captain
 *   crossing into Norway has their phone on ship's time and that is the clock
 *   they are actually working to. The zone abbreviation is always shown
 *   alongside, and Europe/Oslo is shown too whenever the two differ -- the
 *   window was agreed with a Norwegian authority in Norwegian time, so the
 *   captain needs to be able to see both.
 */

export const OSLO = 'Europe/Oslo'

export type Zone = string

export function deviceZone(): Zone {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || OSLO
  } catch {
    return OSLO
  }
}

function fmt(iso: string | Date, zone: Zone, options: Intl.DateTimeFormatOptions): string {
  const date = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(date.getTime())) return '—'
  return new Intl.DateTimeFormat('en-GB', { timeZone: zone, ...options }).format(date)
}

/** "12 Jul 2026, 14:30" */
export function formatDateTime(iso: string | Date, zone: Zone = OSLO): string {
  return fmt(iso, zone, {
    day: '2-digit',
    month: 'short',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
}

/** "12 Jul 2026" */
export function formatDate(iso: string | Date, zone: Zone = OSLO): string {
  return fmt(iso, zone, { day: '2-digit', month: 'short', year: 'numeric' })
}

/** "14:30" */
export function formatTime(iso: string | Date, zone: Zone = OSLO): string {
  return fmt(iso, zone, { hour: '2-digit', minute: '2-digit', hour12: false })
}

export function zoneAbbreviation(iso: string | Date, zone: Zone): string {
  const parts = new Intl.DateTimeFormat('en-GB', {
    timeZone: zone,
    timeZoneName: 'short',
  }).formatToParts(typeof iso === 'string' ? new Date(iso) : iso)
  return parts.find((p) => p.type === 'timeZoneName')?.value ?? ''
}

/**
 * "12 Jul 14:30 → 14 Jul 09:00". Collapses the second date when the window
 * starts and ends on the same day, which most of them do.
 */
export function formatWindow(startIso: string, endIso: string, zone: Zone = OSLO): string {
  const start = new Date(startIso)
  const end = new Date(endIso)
  const sameDay = formatDate(start, zone) === formatDate(end, zone)
  const head = fmt(start, zone, {
    day: '2-digit',
    month: 'short',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  })
  const tail = sameDay
    ? formatTime(end, zone)
    : fmt(end, zone, {
        day: '2-digit',
        month: 'short',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      })
  return `${head} → ${tail}`
}

/**
 * The captain-facing form: device zone, with Oslo in brackets when they differ.
 * A captain on UTC+2 ship's time reading a window agreed in Norwegian time
 * needs both numbers in front of them, not a conversion to do in their head.
 */
export function formatWindowForCaptain(startIso: string, endIso: string): string {
  const local = deviceZone()
  const primary = `${formatWindow(startIso, endIso, local)} ${zoneAbbreviation(startIso, local)}`
  if (sameOffset(startIso, local, OSLO)) return primary
  return `${primary}  (${formatWindow(startIso, endIso, OSLO)} ${zoneAbbreviation(startIso, OSLO)})`
}

function sameOffset(iso: string, a: Zone, b: Zone): boolean {
  return formatDateTime(iso, a) === formatDateTime(iso, b)
}

/** "in 3 h", "2 days ago". Coarse on purpose -- nobody needs "in 2h 47m". */
export function relative(iso: string, now: Date = new Date()): string {
  const then = new Date(iso).getTime()
  if (Number.isNaN(then)) return '—'
  const seconds = Math.round((then - now.getTime()) / 1000)
  const abs = Math.abs(seconds)
  const rtf = new Intl.RelativeTimeFormat('en', { numeric: 'auto' })
  if (abs < 90) return rtf.format(Math.round(seconds / 60), 'minute')
  if (abs < 3600 * 36) return rtf.format(Math.round(seconds / 3600), 'hour')
  if (abs < 86400 * 30) return rtf.format(Math.round(seconds / 86400), 'day')
  return rtf.format(Math.round(seconds / (86400 * 30)), 'month')
}

/**
 * `<input type="datetime-local">` speaks naive local wall-clock time with no
 * zone. These two functions are the only place that boundary is crossed, so the
 * off-by-one-timezone bug has exactly one place it can live.
 */
export function toLocalInputValue(iso: string | Date | null | undefined): string {
  if (!iso) return ''
  const date = typeof iso === 'string' ? new Date(iso) : iso
  if (Number.isNaN(date.getTime())) return ''
  const pad = (n: number) => String(n).padStart(2, '0')
  return (
    `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}` +
    `T${pad(date.getHours())}:${pad(date.getMinutes())}`
  )
}

export function fromLocalInputValue(value: string): string | null {
  if (!value) return null
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? null : date.toISOString()
}

/** Rounded up to the next quarter hour -- a sane default for a window start. */
export function nextQuarterHour(from: Date = new Date()): Date {
  const date = new Date(from)
  date.setSeconds(0, 0)
  date.setMinutes(Math.ceil((date.getMinutes() + 1) / 15) * 15)
  return date
}
